import type { GameEvent, GameStateResponse } from "@kalaporssi/shared";
import type { ConnectionStatus, Subscription } from "./realtime";

export interface GameSyncOptions {
  fetchState: () => Promise<GameStateResponse>;
  subscribe: (handlers: { onEvent: (e: GameEvent) => void; onStatus: (s: ConnectionStatus) => void }) => Subscription;
  onState: (s: GameStateResponse) => void;
  onEvent?: (e: GameEvent) => void;
  onError?: (e: unknown) => void;
  onConnection?: (s: ConnectionStatus) => void;
  /** clientPollIntervalSeconds from seed (SPEC §9). */
  pollIntervalMs: number;
  /** Poll also when connected but no event arrived for this long. */
  quietAfterMs?: number;
  now?: () => number;
}

/**
 * Keeps a game state fresh (SPEC §9): every event triggers a refetch, and the client polls while the
 * realtime connection is down or quiet for more than 15 s. State is only replaced when `version` grows.
 */
export class GameSync {
  private state: GameStateResponse | null = null;
  private connection: ConnectionStatus = "connecting";
  private lastEventAt: number;
  private sub: Subscription | null = null;
  private timer: ReturnType<typeof setInterval> | undefined;
  private inflight: Promise<void> | null = null;
  private again = false;
  private readonly now: () => number;

  constructor(private readonly o: GameSyncOptions) {
    this.now = o.now ?? Date.now;
    this.lastEventAt = this.now();
  }

  start() {
    this.sub = this.o.subscribe({
      onEvent: (e) => {
        this.lastEventAt = this.now();
        this.o.onEvent?.(e);
        if (!this.state || e.version > this.state.version) void this.refresh();
      },
      onStatus: (s) => {
        const wasDown = this.connection !== "connected";
        this.connection = s;
        this.o.onConnection?.(s);
        // Catch up on anything missed while reconnecting.
        if (s === "connected" && wasDown) {
          this.lastEventAt = this.now();
          void this.refresh();
        }
      },
    });
    this.timer = setInterval(() => this.tick(), this.o.pollIntervalMs);
    void this.refresh();
  }

  stop() {
    clearInterval(this.timer);
    this.sub?.close();
    this.sub = null;
  }

  get current() {
    return this.state;
  }

  /** Whether the poll loop would fetch right now. */
  shouldPoll(): boolean {
    return this.connection !== "connected" || this.now() - this.lastEventAt > (this.o.quietAfterMs ?? 15_000);
  }

  private tick() {
    if (this.shouldPoll()) void this.refresh();
  }

  /** Fetches state; concurrent calls coalesce into one extra fetch. */
  refresh(): Promise<void> {
    if (this.inflight) {
      this.again = true;
      return this.inflight;
    }
    this.inflight = (async () => {
      do {
        this.again = false;
        try {
          const s = await this.o.fetchState();
          if (!this.state || s.version >= this.state.version || s.code !== this.state.code) {
            this.state = s;
            this.o.onState(s);
          }
        } catch (e) {
          this.o.onError?.(e);
        }
      } while (this.again);
    })().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }
}
